import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Leaf } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_QUESTIONS = [
  "Which product helps with joint pain?",
  "Piles Care ka price kya hai?",
  "Do you ship all over India?",
];

const GREETING = {
  role: "assistant",
  text: "Namaste! I'm the Eleven Stars wellness assistant. Ask me anything about our Ayurvedic products, prices, ingredients or shipping.",
};

const renderText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p
  );
};

export const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionRef = useRef(
    localStorage.getItem("es_chat_session") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("es_chat_session", id);
        return id;
      })()
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!resp.ok || !resp.body) throw new Error("chat failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  text: copy[copy.length - 1].text + parsed.delta,
                };
                return copy;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: parsed.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry, I couldn't connect right now. Please email us at support@11starwellness.com.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="live-chat-panel"
            className="fixed bottom-40 left-4 sm:left-5 z-50 w-[calc(100vw-2rem)] max-w-sm h-[500px] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-[#E5E0D8] flex flex-col overflow-hidden"
          >
            <div className="bg-[#1A4331] text-[#F9F6F0] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex w-2.5 h-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
                </span>
                <div>
                  <p className="font-display font-semibold text-sm leading-tight">Wellness Assistant</p>
                  <p className="text-[10px] text-[#F9F6F0]/70">Live · replies instantly</p>
                </div>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                className="text-[#F9F6F0]/70 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9F6F0]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#C05C46] text-white rounded-br-sm"
                        : "bg-white border border-[#E5E0D8] text-[#1c2826] rounded-bl-sm"
                    }`}
                  >
                    {m.text ? renderText(m.text) : (busy && i === messages.length - 1 ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#7C9A82]" />
                    ) : null)}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      data-testid={`chat-quick-${q.slice(0, 15).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => send(q)}
                      className="text-xs border border-[#1A4331]/30 text-[#1A4331] rounded-full px-3 py-1.5 hover:bg-[#1A4331] hover:text-white transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-[#E5E0D8] bg-white">
              <div className="flex gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about products, prices, shipping..."
                  className="flex-1 text-sm border border-[#E5E0D8] rounded-full px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1A4331]/30"
                />
                <button
                  data-testid="chat-send-button"
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  className="w-10 h-10 rounded-full bg-[#1A4331] hover:bg-[#C05C46] text-white flex items-center justify-center transition-colors disabled:opacity-40"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="live-chat-button"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-24 left-4 sm:left-5 z-50 w-14 h-14 rounded-full bg-[#1A4331] text-white shadow-xl flex items-center justify-center hover:bg-[#C05C46] transition-colors"
        aria-label="Open live chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && (
          <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center">
            <Leaf className="w-3 h-3 text-[#1A4331]" />
          </span>
        )}
      </motion.button>
    </>
  );
};
